import React, { useState } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles, TableRow, Table } from '@material-ui/core';
import TableComponents from 'components/Table';
import { formatCurrency, formatPercent } from 'utils';
import {
  ExistingPolicyType,
  FundAllocationType
} from 'store/modules/ExistingPolicies/types';
import { PolicyType } from 'store/modules/Policies/types';
import usePolicy from 'hooks/usePolicy';
import Fund from './Fund';

const useStyles = makeStyles({
  icon: {
    transition: '0.3s',
    cursor: 'pointer',
    fontSize: '1.5rem'
  },
  fundsRow: {
    backgroundColor: '#C7C7C7',
    color: '#fff'
  },
  tableCells: {
    paddingLeft: '1rem'
  },
  chartCell: {
    padding: 0
  }
});

interface ExistingPolicyProps {
  policy: ExistingPolicyType;
  isEvalue: boolean;
}

interface RecommendedPolicyProps {
  policy: PolicyType;
  isEvalue: boolean;
}

export const ExistingPolicy = ({ policy, isEvalue }: ExistingPolicyProps) => {
  const [isFundsOpen, setIsFundsOpen] = useState(false);
  const { humanizePolicyPlatform, humanizePolicyProvider, humanizePolicyType } =
    usePolicy();
  const classes = useStyles();
  const { fund_allocations } = policy;
  const areFundsExist = fund_allocations.length > 0;
  const isWrapCash = policy.wrap_amount || policy.wrap_percentage;
  const wrapAmount = policy.wrap_amount
    ? policy.wrap_amount
    : Number(policy.wrap_percentage) * Number(policy.fund_value);
  const wrapPercent = policy.wrap_percentage
    ? Number(policy.wrap_percentage) * 100
    : (Number(policy.wrap_amount) / Number(policy.fund_value)) * 100;
  return (
    <>
      <TableRow>
        <TableComponents.TableCell width="300px" className={classes.tableCells}>
          {policy.owner.display_name}
        </TableComponents.TableCell>
        <TableComponents.TableCell>
          {humanizePolicyPlatform(policy.platform)}
        </TableComponents.TableCell>
        <TableComponents.TableCell>
          {humanizePolicyProvider(policy.provider)}
        </TableComponents.TableCell>
        <TableComponents.TableCell>
          {humanizePolicyType(policy.type)}
        </TableComponents.TableCell>
        <TableComponents.TableCell>
          {formatCurrency(policy.fund_value)}
        </TableComponents.TableCell>

        <TableComponents.TableCell>
          {formatCurrency(policy.transfer_value)}
        </TableComponents.TableCell>

        <TableComponents.TableCell>
          {formatPercent(Number(policy.total_ongoing_charge) * 100)}
        </TableComponents.TableCell>
        {isEvalue ? (
          <TableComponents.TableCell>
            {policy.risk_score || '-'}
          </TableComponents.TableCell>
        ) : (
          <>
            <TableComponents.TableCell>
              {formatPercent(policy.morningstar_risk_analysis.growth_score)}
            </TableComponents.TableCell>
            <TableComponents.TableCell>
              {`${policy.morningstar_risk_analysis.range?.low} - ${policy.morningstar_risk_analysis.range?.high}`}
            </TableComponents.TableCell>
          </>
        )}
        <TableComponents.TableCell width="60px">
          {areFundsExist && (
            <ExpandMoreIcon
              className={classes.icon}
              width="20px"
              style={{
                transform: isFundsOpen ? 'rotate(180deg)' : 'rotate(0)'
              }}
              onClick={() => setIsFundsOpen(!isFundsOpen)}
            ></ExpandMoreIcon>
          )}
        </TableComponents.TableCell>
      </TableRow>
      {isFundsOpen && (
        <>
          <TableRow>
            <TableComponents.TableCell colSpan={9} style={{ padding: 0 }}>
              <Table>
                <TableComponents.TableHead
                  cells={[
                    'Fund',
                    'Sector',
                    '£',
                    '%',
                    'OCF',
                    'Transaction cost',
                    'Total fund charge',
                    '1yr',
                    '3yr',
                    '5yr',
                    ''
                  ]}
                />
                <TableComponents.TableBody>
                  {fund_allocations.map(
                    (fundInstance: FundAllocationType, index: number) => {
                      return <Fund fundInstance={fundInstance} key={index} />;
                    }
                  )}
                  {isWrapCash && (
                    <TableRow>
                      <TableComponents.TableCell>
                        Wrap Cash
                      </TableComponents.TableCell>
                      <TableComponents.TableCell />
                      <TableComponents.TableCell>
                        {formatCurrency(wrapAmount)}
                      </TableComponents.TableCell>
                      <TableComponents.TableCell>
                        {formatPercent(wrapPercent)}
                      </TableComponents.TableCell>
                      <TableComponents.TableCell></TableComponents.TableCell>
                      <TableComponents.TableCell></TableComponents.TableCell>
                      <TableComponents.TableCell></TableComponents.TableCell>
                    </TableRow>
                  )}
                </TableComponents.TableBody>
              </Table>
            </TableComponents.TableCell>
          </TableRow>
        </>
      )}
    </>
  );
};

export const RecommendedPolicy = ({
  policy,
  isEvalue
}: RecommendedPolicyProps) => {
  const [isFundsOpen, setIsFundsOpen] = useState(false);
  const { humanizePolicyPlatform, humanizePolicyProvider, humanizePolicyType } =
    usePolicy();
  const classes = useStyles();
  const { fund_allocations } = policy;
  const areFundsExist = fund_allocations.length > 0;

  return (
    <>
      <TableRow>
        <TableComponents.TableCell width="300px" className={classes.tableCells}>
          {policy.owner.display_name}
        </TableComponents.TableCell>
        <TableComponents.TableCell>
          {humanizePolicyPlatform(policy.platform)}
        </TableComponents.TableCell>
        <TableComponents.TableCell>
          {humanizePolicyProvider(policy.provider)}
        </TableComponents.TableCell>
        <TableComponents.TableCell>
          {humanizePolicyType(policy.type)}
        </TableComponents.TableCell>
        <TableComponents.TableCell>
          {formatCurrency(policy.fund_value_initial)}
        </TableComponents.TableCell>

        <TableComponents.TableCell>
          {formatCurrency(policy.annualised_contribution || 0)}
        </TableComponents.TableCell>

        <TableComponents.TableCell>
          {formatCurrency(policy.annualised_withdrawal || 0)}
        </TableComponents.TableCell>

        <TableComponents.TableCell>
          {formatPercent(Number(policy.total_ongoing_charge) * 100)}
        </TableComponents.TableCell>
        {isEvalue ? (
          <TableComponents.TableCell>
            {policy.risk_score || '-'}
          </TableComponents.TableCell>
        ) : (
          <>
            <TableComponents.TableCell>
              {formatPercent(policy.morningstar_risk_analysis.growth_score)}
            </TableComponents.TableCell>
            <TableComponents.TableCell>
              {`${policy.morningstar_risk_analysis.range?.low} - ${policy.morningstar_risk_analysis.range?.high}`}
            </TableComponents.TableCell>
          </>
        )}
        <TableComponents.TableCell width="60px">
          {areFundsExist && (
            <ExpandMoreIcon
              className={classes.icon}
              width="20px"
              style={{
                transform: isFundsOpen ? 'rotate(180deg)' : 'rotate(0)'
              }}
              onClick={() => setIsFundsOpen(!isFundsOpen)}
            ></ExpandMoreIcon>
          )}
        </TableComponents.TableCell>
      </TableRow>
      {isFundsOpen && (
        <>
          <TableRow>
            <TableComponents.TableCell colSpan={10} style={{ padding: 0 }}>
              <Table>
                <TableComponents.TableHead
                  cells={[
                    'Fund',
                    'Sector',
                    '£',
                    '%',
                    'OCF',
                    'Transaction cost',
                    'Total fund charge',
                    '1yr',
                    '3yr',
                    '5yr',
                    ''
                  ]}
                />
                <TableComponents.TableBody>
                  {fund_allocations.map(
                    (fundInstance: FundAllocationType, index: number) => {
                      return <Fund fundInstance={fundInstance} key={index} />;
                    }
                  )}
                </TableComponents.TableBody>
              </Table>
            </TableComponents.TableCell>
          </TableRow>
        </>
      )}
    </>
  );
};
